import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormattedMoney } from '@repo/design-system-kit';
import cx from 'clsx';
import { useSegment } from '@repo/poly-hooks/segment';
import {
  IconStars48,
  IconApple,
  IconStars47,
  IconGooglePlay,
  IconStars46,
  IconTrustpilot,
} from 'receivable-invoices-js/react/assets';
import styles from './referral.module.css';

export interface ReferralProps {
  companyName: string;
  referralUrl: string;
}

export function Referral({ companyName, referralUrl }: ReferralProps): ReactNode {
  const segment = useSegment();

  const handleReferralClick = (): void => {
    segment.track('public-page-payment_referral_clicked');
  };

  return (
    <figcaption className={styles.figcaption}>
      <span className="title-3 mb-8" data-test-invoices-referral-title>
        <FormattedMessage id="invoice.pay.illustration.title" />
      </span>
      <span className="body-1 mb-24" data-test-invoices-referral-subtitle>
        <FormattedMessage id="invoice.pay.illustration.subtitle" values={{ companyName }} />
      </span>
      <a
        className="btn btn--primary btn--large mb-8"
        data-test-invoices-referral-cta
        href={referralUrl}
        onClick={handleReferralClick}
        rel="noopener noreferrer"
        target="_blank"
      >
        <FormattedMessage id="invoice.pay.illustration.open-account-cta" />
      </a>
      <span className="caption mb-24">
        <FormattedMessage
          id="invoice.pay.illustration.referral-disclaimer"
          values={{
            amount: <FormattedMoney amount={80} currency="EUR" token="money-symbol-compact" />,
          }}
        />
      </span>
      <hr className={cx(styles.figDivider, 'mb-24')} />
      <section className={styles.stores}>
        <article>
          <div className={cx(styles.stars, 'mb-8')}>
            <div>
              <IconStars48 aria-hidden="true" />
            </div>
          </div>
          <div className={cx(styles.storeText, styles.appstore, 'body-2')}>
            <IconApple aria-hidden="true" />
            <FormattedMessage id="invoice.pay.illustration.scores.app-store" />
          </div>
        </article>
        <article>
          <div className={cx(styles.stars, 'mb-8')}>
            <div>
              <IconStars47 aria-hidden="true" />
            </div>
          </div>
          <div className={cx(styles.storeText, styles.playstore, 'body-2')}>
            <IconGooglePlay aria-hidden="true" />
            <FormattedMessage id="invoice.pay.illustration.scores.play-store" />
          </div>
        </article>
        <article>
          <div className={cx(styles.stars, 'mb-8')}>
            <div>
              <IconStars46 aria-hidden="true" />
            </div>
          </div>
          <div className={cx(styles.storeText, styles.trustpilot, 'body-2')}>
            <IconTrustpilot aria-hidden="true" />
            <FormattedMessage id="invoice.pay.illustration.scores.trustpilot" />
          </div>
        </article>
      </section>
    </figcaption>
  );
}
